import React, { useEffect, useMemo, useState } from 'react'
import { Button } from 'react-bootstrap'
import FontAwesomeIcon from '@fortawesome/react-fontawesome'
import { faPaperPlane } from '@fortawesome/free-solid-svg-icons'
import { end_point, getParams } from './helper'

const formatMessageTime = (value) => {
  if (!value) {
    return ''
  }

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return ''
  }

  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

const normalizeMessages = (message) => {
  if (Array.isArray(message)) {
    return message
  }

  if (message && Array.isArray(message.messages)) {
    return message.messages
  }

  return []
}

const normalizeResponse = (payload) => {
  if (!payload) {
    return null
  }

  if (Array.isArray(payload.message)) {
    return payload.message
  }

  if (payload.message && Array.isArray(payload.message.messages)) {
    return payload.message.messages
  }

  return payload.message || payload
}

const getAthleteLabel = (athlete) => {
  if (!athlete) {
    return 'this athlete'
  }

  return `${athlete.first_name || ''} ${athlete.last_name || ''}`.trim() || 'this athlete'
}

export default function AthleteMessagesPanel({
  athlete,
  organizationId,
  token,
  conversationType = 'athlete',
  conversationKey = null,
  conversationLabel = null,
  programId = null,
  groupName = null,
  currentUserType = 'parent',
  visible = true,
  onUnreadChange,
  className = '',
  panelTitle = 'Message',
  heading = null,
}) {
  const [messages, setMessages] = useState([])
  const [draft, setDraft] = useState('')
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')

  const athleteId = athlete && athlete._id ? athlete._id : null
  const effectiveToken = token || getParams().jwt
  const endpoint = useMemo(() => end_point(), [])
  const resolvedConversationType = conversationType === 'group' ? 'group' : 'athlete'
  const resolvedConversationKey = resolvedConversationType === 'group'
    ? (conversationKey || groupName || '')
    : athleteId
  const resolvedConversationLabel = resolvedConversationType === 'group'
    ? (conversationLabel || groupName || 'Team')
    : (conversationLabel || getAthleteLabel(athlete))

  const cleanQueryParams = (queryParams = {}) => {
    return Object.keys(queryParams).reduce((acc, key) => {
      const value = queryParams[key]
      if (value === null || value === undefined || value === '' || value === 'null' || value === 'undefined') {
        return acc
      }
      acc[key] = value
      return acc
    }, {})
  }

  const requestJson = async (method, body, queryParams = {}) => {
    const cleanedQueryParams = cleanQueryParams(queryParams)
    const queryString = Object.keys(cleanedQueryParams).length
      ? `?${new URLSearchParams(cleanedQueryParams).toString()}`
      : ''

    const response = await fetch(`${endpoint}/messages${queryString}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        token: effectiveToken,
        organization_id: organizationId,
      },
      body: body ? JSON.stringify(body) : undefined,
    })

    const payload = await response.json()
    if (!response.ok) {
      throw new Error(payload && payload.errorMessage ? payload.errorMessage : 'Message request failed')
    }

    return payload
  }

  const unreadCount = useMemo(() => {
    if (resolvedConversationType === 'group') {
      return 0
    }

    if (!messages.length) {
      return 0
    }

    return messages.filter((message) => {
      if (currentUserType === 'parent') {
        return !message.read_by_parent_at && message.sender_type !== 'parent'
      }

      return !message.read_by_admin_at && message.sender_type === 'parent'
    }).length
  }, [currentUserType, messages])

  useEffect(() => {
    if (typeof onUnreadChange === 'function') {
      onUnreadChange(unreadCount)
    }
  }, [onUnreadChange, unreadCount])

  useEffect(() => {
    if (!visible || !resolvedConversationKey || !organizationId || !effectiveToken) {
      return
    }

    let cancelled = false
    setLoading(true)
    setError('')

    const markRead = (loadedMessages) => {
      if (!loadedMessages.length) {
        return
      }

      if (resolvedConversationType === 'group') {
        requestJson('PATCH', {
          conversation_type: 'group',
          conversation_key: resolvedConversationKey,
          organization_id: organizationId,
          program_id: programId || undefined,
          group_name: groupName || undefined,
        }).catch(() => null)
        return
      }

      const needsReadUpdate = loadedMessages.some((message) => {
        if (currentUserType === 'parent') {
          return !message.read_by_parent_at && message.sender_type !== 'parent'
        }

        return !message.read_by_admin_at && message.sender_type === 'parent'
      })

      if (!needsReadUpdate) {
        return
      }

      requestJson('PATCH', {
        conversation_type: resolvedConversationType,
        athlete_id: athleteId,
        conversation_key: resolvedConversationKey,
        organization_id: organizationId,
        read_as: currentUserType,
        program_id: programId || undefined,
        group_name: groupName || undefined,
      }).catch(() => null)
    }

    requestJson('GET', null, {
      conversation_type: resolvedConversationType,
      athlete_id: athleteId,
      conversation_key: resolvedConversationKey,
      organization_id: organizationId,
      token: effectiveToken,
      program_id: programId || undefined,
      group_name: groupName || undefined,
    }).then((resp) => {
      if (cancelled) {
        return
      }

      const loadedMessages = normalizeMessages(resp && resp.message)
      setMessages(loadedMessages)
      setLoading(false)
      markRead(loadedMessages)
    }).catch((ajaxError) => {
      if (cancelled) {
        return
      }

      setMessages([])
      setLoading(false)
      setError('Unable to load messages right now.')
      console.error('message load failed', ajaxError)
    })

    return () => {
      cancelled = true
    }
  }, [athleteId, currentUserType, effectiveToken, endpoint, groupName, organizationId, programId, resolvedConversationKey, resolvedConversationType, visible])

  const handleSend = () => {
    if (!draft.trim() || !resolvedConversationKey || !organizationId || !effectiveToken) {
      return
    }

    setSending(true)
    setError('')

    const tempId = `temp-${Date.now()}`
    const optimisticMessage = {
      _id: tempId,
      athlete_id: athleteId,
      conversation_type: resolvedConversationType,
      conversation_key: resolvedConversationKey,
      body: draft.trim(),
      created_at: new Date().toISOString(),
      sender_type: currentUserType === 'parent' ? 'parent' : 'admin',
      sender_name: currentUserType === 'parent' ? 'You' : 'You',
      pending: true,
    }

    setMessages((currentMessages) => [...currentMessages, optimisticMessage])

    requestJson('POST', {
        conversation_type: resolvedConversationType,
        conversation_key: resolvedConversationKey,
        athlete_id: athleteId,
        organization_id: organizationId,
        program_id: programId || (athlete && athlete.program_id ? athlete.program_id : null),
        group_name: groupName || conversationLabel || null,
        body: draft.trim(),
      }).then((payload) => {
      setDraft('')
      setSending(false)
      const savedMessage = normalizeResponse(payload)
      if (savedMessage && savedMessage._id) {
        setMessages((currentMessages) => currentMessages.map((message) => (
          message._id === tempId ? savedMessage : message
        )))
      } else {
        setMessages((currentMessages) => currentMessages.filter((message) => message._id !== tempId))
      }
    }).catch((ajaxError) => {
      setSending(false)
      setMessages((currentMessages) => currentMessages.filter((message) => message._id !== tempId))
      setError('Unable to send this message.')
      console.error('message send failed', ajaxError)
    })
  }

  const thread = messages.length ? messages : []
  const athleteLabel = resolvedConversationLabel
  const canSend = draft.trim().length > 0 && !sending

  return (
    <div className={`athlete-messages-panel ${className}`.trim()}>
      <div className="athlete-messages-panel-header">
        <div>
          <div className="programs-page-kicker">{panelTitle}</div>
          <h4>{heading || `Conversation with ${athleteLabel}`}</h4>
          <div className="athlete-messages-panel-subtitle">
            {currentUserType === 'parent' ? 'Reply to the coaching/admin team.' : 'Visible to the parent profile for this athlete.'}
          </div>
        </div>
        {unreadCount > 0 ? <span className="athlete-messages-unread-badge">{unreadCount} unread</span> : null}
      </div>

      <div className="athlete-messages-thread" aria-live="polite">
        {loading ? (
          <div className="athlete-messages-empty">Loading messages...</div>
        ) : thread.length ? (
          thread.map((message) => {
            const isMine = currentUserType === 'parent'
              ? message.sender_type === 'parent'
              : message.sender_type !== 'parent'

            return (
              <div
                key={message._id || `${message.created_at}-${message.sender_id}`}
                className={isMine ? 'athlete-message-bubble mine' : 'athlete-message-bubble theirs'}
              >
                <div className="athlete-message-meta">
                  <span>{message.sender_name || (isMine ? 'You' : 'Admin')}</span>
                  <span>{formatMessageTime(message.created_at)}</span>
                </div>
                <div className="athlete-message-body">{message.body}</div>
              </div>
            )
          })
        ) : (
          <div className="athlete-messages-empty">No messages yet. Start the conversation below.</div>
        )}
      </div>

      {error ? <div className="athlete-messages-error">{error}</div> : null}

      <textarea
        className="opaque-grey athlete-messages-input"
        rows={4}
        placeholder="Write a message"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
      />

      <div className="athlete-messages-actions">
        <Button className="rostersbutton athlete-messages-send" disabled={!canSend} onClick={handleSend}>
          {sending ? 'Sending...' : <span><FontAwesomeIcon icon={faPaperPlane} /> Send</span>}
        </Button>
      </div>
    </div>
  )
}
